import React, { useState, useEffect, useCallback } from "react";
import { TabsContent } from "../ui/tabs";
import { Copy, Check, Trash2, RefreshCw, Plus, Key, Clock, Shield, ExternalLink, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { useAuth } from "../../AuthContext";
import McpAuditCard from "./McpAuditCard";
import McpUsageCard from "./McpUsageCard";
import McpProRequestsCard from "./McpProRequestsCard";

const MCP_TIERS = [
  { value: "", label: "Standard REST key (X-API-Key)" },
  { value: "open", label: "MCP — Open (free tier)" },
  { value: "pro", label: "MCP — Pro (ask_tourismintel, 100 q/mo)" },
  { value: "admin", label: "MCP — Admin (ops tools, /api/mcp-admin)" },
];

const webAlign = (k) => {
  if (k.account_is_pro === null || k.account_is_pro === undefined) return { label: "web:—", state: "none" };
  const expected = k.account_is_pro ? "pro" : "open";
  return {
    label: `web:${k.account_is_pro ? "pro" : "free"}${expected === k.tier ? " ✓" : " ✗"}`,
    state: expected === k.tier ? "ok" : "mismatch",
  };
};


export default function ExternalApiTab({ dm }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [allowWrite, setAllowWrite] = useState(false);
  const [mcpTier, setMcpTier] = useState("");
  const [cloneFrom, setCloneFrom] = useState("");        // rotation: copy scopes from an existing key
  const [addDocsWrite, setAddDocsWrite] = useState(false); // Pulse Doc Catalog push scope
  const [changingTier, setChangingTier] = useState(null);
  const [generatedKey, setGeneratedKey] = useState(null); // shown once
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const { authHeader } = useAuth();

  const [loadError, setLoadError] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/admin/external-keys`, { headers: authHeader() });
      setKeys(data.keys || []);
    } catch (err) {
      // Capture status so the user can see WHY (401 vs 500 vs network) and
      // retry without a full page reload. The global axios interceptor in
      // AuthContext now verifies session via /auth/me before logging out,
      // so a single 401 here won't kick the user back to /auth — they can
      // simply click "Retry".
      const status = err?.response?.status;
      setLoadError({
        status,
        message: status === 401
          ? "Session lost on this request — try again, or refresh the page."
          : status === 403
            ? "You don't have admin permissions for this resource."
            : (err?.response?.data?.detail || "Failed to load external API keys"),
      });
      // Suppress the toast for 401 (interceptor handles real expiry); show
      // toast only for other statuses so users immediately know something
      // went wrong.
      if (status !== 401) toast.error("Failed to load external API keys");
    } finally {
      setLoading(false);
    }
    // `authHeader` from useAuth is stable (useCallback([])), so this callback
    // is also stable across re-renders → the useEffect below only fires once
    // on mount. Earlier this was `[headers]` with `const headers = authHeader()`
    // recomputed at render time, which created a fresh object literal on every
    // render → infinite re-render loop → "Failed to load…" toast stacking in
    // production.
  }, [authHeader]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const generateKey = async () => {
    if (!newKeyName.trim()) { toast.error("Enter a label for the key"); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/external-keys/generate`,
        {
          name: newKeyName.trim(),
          allow_write: !mcpTier && allowWrite,
          ...(mcpTier ? { tier: mcpTier } : {}),
          ...(!mcpTier && cloneFrom ? { clone_scopes_from: cloneFrom } : {}),
          ...(!mcpTier && addDocsWrite ? { add_scopes: ["documents:write"] } : {}),
        },
        { headers: authHeader() }
      );
      setGeneratedKey(data);
      setNewKeyName("");
      setAllowWrite(false);
      setMcpTier("");
      setCloneFrom("");
      setAddDocsWrite(false);
      await loadKeys();
      toast.success(mcpTier
        ? `MCP ${mcpTier} key generated — copy it now!`
        : allowWrite
          ? "Write-capable API key generated — copy it now!"
          : "API key generated — copy it now!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate key");
    } finally {
      setGenerating(false);
    }
  };

  const revokeKey = async (keyId, keyName) => {
    if (!window.confirm(`Revoke key "${keyName}"? This cannot be undone.`)) return;
    setRevoking(keyId);
    try {
      await axios.delete(`${API}/admin/external-keys/${keyId}`, { headers: authHeader() });
      setKeys(prev => prev.filter(k => k.id !== keyId));
      toast.success("Key revoked");
    } catch {
      toast.error("Failed to revoke key");
    } finally {
      setRevoking(null);
    }
  };

  const changeTier = async (keyId, tier) => {
    setChangingTier(keyId);
    try {
      await axios.patch(`${API}/admin/external-keys/${keyId}/tier`, { tier }, { headers: authHeader() });
      setKeys(prev => prev.map(k => (k.id === keyId ? { ...k, tier } : k)));
      toast.success(`Tier changed to ${tier}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change tier");
    } finally {
      setChangingTier(null);
    }
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Key copied to clipboard");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const apiBase = window.location.origin;

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };
  const sub = `category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`;
  const pill = (color) => `px-2 py-0.5 rounded-full category-tag ${
    color === "green"  ? (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700") :
    color === "red"    ? (dm ? "bg-red-400/10 text-red-400"         : "bg-red-50 text-red-600")        :
    color === "slate"  ? (dm ? "bg-slate-700 text-slate-400"        : "bg-slate-100 text-slate-600")   : ""
  }`;

  return (
    <TabsContent value="external-api">
      <div className="max-w-2xl space-y-6">

        {/* Header */}
        <div>
          <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>
            External API
          </h2>
          <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Machine-to-machine access to the TourismIntel feed. Use API keys in the <code className="px-1 rounded">X-API-Key</code> header.
          </p>
        </div>

        {/* Endpoint Reference */}
        <div className={`${card} space-y-3`} style={cardShadow}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              <ExternalLink className="w-4 h-4" /> Endpoint Reference
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>12 PUBLIC + 2 OPS</span>
            </h3>
            <a
              href="/developers"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-public-docs-link"
              className={`text-xs font-medium flex items-center gap-1 ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}
            >
              Full docs (BETA) <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="/developer-api-docs.md"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-api-md-link"
              className={`text-xs font-medium flex items-center gap-1 ${dm ? "text-cyan-400 hover:text-cyan-300" : "text-[#006874] hover:text-[#004f58]"}`}
              title="Markdown reference (good for LLM context / agent briefing)"
            >
              .md <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {[
            { method: "GET",  path: "/api/external/feed",           desc: "Paginated article feed (last 90d)", params: "?page=1&limit=20&search=&category=&include_reddit=false" },
            { method: "GET",  path: "/api/external/brief",          desc: "Latest Brief of the Week (content + metadata)", params: null },
            { method: "GET",  path: "/api/external/podcast",        desc: "Published podcast episodes + latest shortcut",   params: "?limit=10" },
            { method: "GET",  path: "/api/external/newsletter",     desc: "Published newsletters (HTML body + metadata)",   params: "?limit=10" },
            { method: "GET",  path: "/api/external/top-performers", desc: "Ranked by signal_score · clicks · top topics",   params: "?limit=10&days=7&exclude_reddit=true" },
            { method: "GET",  path: "/api/external/trending",       desc: "Articles flagged trending (4+ clicks within 6h)", params: "?limit=20&hours=24", badge: "NEW" },
            { method: "GET",  path: "/api/external/sources",        desc: "Editorial source catalog with aggregates",        params: "?days=30&exclude_reddit=true", badge: "NEW" },
            { method: "GET",  path: "/api/external/weekly-ai-summary", desc: "AI-generated thematic editorial digest (GPT-4o · 24h cache)", params: "?window_days=7&language=en&force_refresh=false", badge: "NEW" },
            { method: "GET",  path: "/api/external/clusters",       desc: "Raw story clusters (3+ sources covering same news)",  params: "?viral_only=true&hours=72", badge: "NEW" },
            { method: "GET",  path: "/api/external/reports",        desc: "Curated industry reports & research (84+ seed, weekly Firecrawl-discovered)", params: "?category=Istituzioni%20globali&limit=50", badge: "NEW" },
            { method: "GET",  path: "/api/external/reports/categories", desc: "Report categories with item counts",          params: null, badge: "NEW" },
            { method: "GET",  path: "/api/external/reports/sources",    desc: "The 155 curated source organisations",        params: "?category=Travel%20tech%20e%20infrastrutture&limit=200", badge: "NEW" },
            { method: "POST", path: "/api/external/refresh",        desc: "Trigger news scraping (started/skipped_reason: cooldown 20 min from last completion)", params: null },
            { method: "GET",  path: "/api/scraping-status",         desc: "Last scrape run status (running/completed/failed/timeout/killed_stale)",        params: null, noAuth: true },
            { method: "GET",  path: "/api/health",                  desc: "Liveness + ingestion freshness (UptimeRobot-ready). Sets status=degraded if last article >24h or run hanging >1h", params: null, noAuth: true, badge: "NEW" },
          ].map(ep => {
            const methodColor = ep.method === "POST"
              ? (dm ? "bg-blue-400/10 text-blue-400" : "bg-blue-50 text-blue-700")
              : (dm ? "bg-emerald-400/10 text-emerald-400" : "bg-emerald-50 text-emerald-700");
            return (
              <div key={ep.path} className={`rounded-xl p-3 ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${methodColor}`}>{ep.method}</span>
                  <code className={`text-xs font-mono ${dm ? "text-cyan-400" : "text-[#006874]"}`}>{apiBase}{ep.path}</code>
                  {ep.badge && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider ${dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700"}`}>{ep.badge}</span>
                  )}
                  {ep.noAuth && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${dm ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-600"}`}>no auth</span>
                  )}
                </div>
                <p className={`text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{ep.desc}</p>
                {ep.params && <p className={`text-[10px] font-mono mt-1 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{ep.params}</p>}
              </div>
            );
          })}

          <div className={`rounded-xl p-3 ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}>
            <p className={`text-xs font-semibold mb-1 ${dm ? "text-slate-300" : "text-[#191c1d]"}`}>Example request</p>
            <pre className={`text-[10px] font-mono leading-relaxed overflow-x-auto ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>{`curl "${apiBase}/api/external/top-performers?days=7&limit=10" \\
  -H "X-API-Key: ti_live_YOUR_KEY_HERE"`}
            </pre>
          </div>
        </div>

        {/* Generated Key Banner */}
        {generatedKey && (
          <div className={`rounded-2xl p-5 border ${dm ? "bg-amber-400/5 border-amber-400/20" : "bg-amber-50 border-amber-200"}`}>
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${dm ? "text-amber-300" : "text-amber-800"}`}>
                  Save this key now — it won&apos;t be shown again
                </p>
                <p className={`text-xs mt-0.5 ${dm ? "text-amber-400/70" : "text-amber-700"}`}>
                  Label: <strong>{generatedKey.name}</strong>
                  {generatedKey.tier && <> · MCP tier: <strong>{generatedKey.tier}</strong> · endpoint: <code>{generatedKey.tier === "admin" ? "/api/mcp-admin" : "/api/mcp"}</code> · header <code>Authorization: Bearer</code> or <code>x-api-key</code> (Claude.ai connector)</>}
                </p>
                {generatedKey.scopes && generatedKey.scopes.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {generatedKey.scopes.map(s => (
                      <span
                        key={s}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-mono ${
                          s === "autopilot:write"
                            ? (dm ? "bg-amber-400/20 text-amber-300" : "bg-amber-100 text-amber-800")
                            : (dm ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-700")
                        }`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={`flex items-center gap-2 p-3 rounded-xl font-mono text-xs break-all ${dm ? "bg-[#0c1011] text-cyan-400" : "bg-white text-[#006874]"}`}>
              <span className="flex-1">{generatedKey.key}</span>
              <button
                onClick={() => copyKey(generatedKey.key)}
                data-testid="copy-generated-key-btn"
                className={`flex-shrink-0 p-2 rounded-lg transition-colors ${dm ? "hover:bg-white/10" : "hover:bg-slate-100"}`}
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => setGeneratedKey(null)}
              className={`mt-3 text-xs ${dm ? "text-slate-600 hover:text-slate-400" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}
            >
              I&apos;ve saved it, dismiss
            </button>
          </div>
        )}

        {/* Generate New Key */}
        <div className={`${card}`} style={cardShadow}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Plus className="w-4 h-4" /> Generate New Key
          </h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && generateKey()}
              placeholder='Label, e.g. "My Mobile App"'
              data-testid="new-key-name-input"
              className={`flex-1 px-3 py-2 rounded-xl text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200 placeholder:text-slate-600" : "bg-[#f3f4f5] text-[#191c1d] placeholder:text-[#bac9cc]"}`}
            />
            <button
              onClick={generateKey}
              disabled={generating || !newKeyName.trim()}
              data-testid="generate-key-btn"
              className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}
            >
              {generating ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
              Generate
            </button>
          </div>

          {/* MCP tier selector — MCP keys use Bearer auth on /api/mcp(-admin) */}
          <div className="mt-3">
            <label className={`text-xs font-medium block mb-1.5 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
              Key type
            </label>
            <select
              value={mcpTier}
              onChange={e => setMcpTier(e.target.value)}
              data-testid="mcp-tier-select"
              className={`w-full px-3 py-2 rounded-xl text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}
            >
              {MCP_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {mcpTier && (
              <p className={`text-[11px] mt-1.5 leading-snug ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                MCP keys authenticate with <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>Authorization: Bearer</code> (or{" "}
                <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`} data-testid="mcp-tier-help-x-api-key">X-API-Key</code> — required by Claude.ai custom connectors, which reserve the Authorization header) on{" "}
                <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>{mcpTier === "admin" ? "/api/mcp-admin" : "/api/mcp"}</code>{" "}
                and cannot call the REST external API. Tier can be changed later.
              </p>
            )}
          </div>

          {/* Write scope toggle — for Mission Control / agent integrations */}
          {!mcpTier && (
          <label
            htmlFor="allow-write-checkbox"
            className={`mt-3 flex items-start gap-2.5 p-3 rounded-xl cursor-pointer transition-colors ${
              allowWrite
                ? (dm ? "bg-amber-400/10 border border-amber-400/30" : "bg-amber-50 border border-amber-200")
                : (dm ? "bg-[#0c1011] border border-transparent hover:border-white/5" : "bg-[#f3f4f5] border border-transparent hover:border-black/5")
            }`}
          >
            <input
              id="allow-write-checkbox"
              type="checkbox"
              checked={allowWrite}
              onChange={e => setAllowWrite(e.target.checked)}
              data-testid="allow-write-checkbox"
              className={`mt-0.5 w-4 h-4 rounded ${dm ? "accent-amber-400" : "accent-amber-600"}`}
            />
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                Allow write actions (autopilot approve / reject)
              </p>
              <p className={`text-[11px] mt-0.5 leading-snug ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                Required for Mission Control to call <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>/api/external/autopilot/approve</code> and <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>/reject</code>. Adds the <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>autopilot:write</code> scope. Cannot be added later — revoke and regenerate if you change your mind.
              </p>
            </div>
          </label>
          )}

          {/* Rotation helper (31/8): a new key born with ALL the scopes of an
              existing key (+ optionally documents:write). Write scopes are
              never added to an existing key — rotation is the sanctioned path. */}
          {!mcpTier && (
            <div className="mt-3 space-y-2">
              <label className={`text-xs font-medium block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                Clone scopes from existing key (rotation)
              </label>
              <select
                value={cloneFrom}
                onChange={e => setCloneFrom(e.target.value)}
                data-testid="clone-scopes-select"
                className={`w-full px-3 py-2 rounded-xl text-sm border-0 outline-none ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5] text-[#191c1d]"}`}
              >
                <option value="">— none (default scopes) —</option>
                {keys.filter(k => k.is_active && !k.tier).map(k => (
                  <option key={k.id} value={k.id}>
                    {k.name} ({k.key_prefix}… · {(k.scopes || ["read"]).join(", ")})
                  </option>
                ))}
              </select>
              <label
                htmlFor="docs-write-checkbox"
                className={`flex items-start gap-2.5 p-3 rounded-xl cursor-pointer transition-colors ${
                  addDocsWrite
                    ? (dm ? "bg-sky-400/10 border border-sky-400/30" : "bg-sky-50 border border-sky-200")
                    : (dm ? "bg-[#0c1011] border border-transparent hover:border-white/5" : "bg-[#f3f4f5] border border-transparent hover:border-black/5")
                }`}
              >
                <input
                  id="docs-write-checkbox"
                  type="checkbox"
                  checked={addDocsWrite}
                  onChange={e => setAddDocsWrite(e.target.checked)}
                  data-testid="docs-write-checkbox"
                  className={`mt-0.5 w-4 h-4 rounded ${dm ? "accent-sky-400" : "accent-sky-600"}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                    Add documents:write (Pulse Doc Catalog push)
                  </p>
                  <p className={`text-[11px] mt-0.5 leading-snug ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                    Lets Mission Control push documents via <code className={`px-1 rounded ${dm ? "bg-[#252e2f]" : "bg-white"}`}>POST /api/external/pulse/documents</code>.
                    Combine with &quot;clone scopes&quot; above to rotate the MC key without breaking its existing cron jobs.
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Active Keys List */}
        <div className={`${card}`} style={cardShadow}>
          <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Shield className="w-4 h-4" /> Active Keys
            {!loading && <span className={`ml-auto ${pill("slate")}`}>{keys.filter(k => k.is_active).length} active</span>}
          </h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
            </div>
          ) : loadError ? (
            <div
              data-testid="external-keys-load-error"
              className={`rounded-xl p-4 flex items-start gap-3 ${
                dm ? "bg-amber-400/5 border border-amber-400/20" : "bg-amber-50 border border-amber-200"
              }`}
            >
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${dm ? "text-amber-300" : "text-amber-800"}`}>
                  Couldn&apos;t load keys{loadError.status ? ` (HTTP ${loadError.status})` : ""}
                </p>
                <p className={`text-xs mt-0.5 ${dm ? "text-amber-400/70" : "text-amber-700"}`}>
                  {loadError.message}
                </p>
              </div>
              <button
                onClick={loadKeys}
                data-testid="external-keys-retry-btn"
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                  dm ? "bg-amber-400 text-[#001f24] hover:bg-amber-300" : "bg-amber-600 text-white hover:bg-amber-700"
                }`}
              >
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            </div>
          ) : keys.length === 0 ? (
            <p className={`text-sm text-center py-8 ${sub}`}>No external API keys yet. Generate one above.</p>
          ) : (
            <div className="space-y-3">
              {keys.map(k => (
                <div
                  key={k.id}
                  data-testid={`external-key-row-${k.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl ${dm ? "bg-[#0c1011]" : "bg-[#f3f4f5]"}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{k.name}</span>
                      <span className={pill(k.is_active ? "green" : "red")}>{k.is_active ? "Active" : "Revoked"}</span>
                      {k.tier && (
                        <span
                          data-testid={`key-tier-badge-${k.id}`}
                          title={`MCP ${k.tier} key — Bearer auth on ${k.tier === "admin" ? "/api/mcp-admin" : "/api/mcp"}`}
                          className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${
                            k.tier === "admin"
                              ? (dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700")
                              : k.tier === "pro"
                                ? (dm ? "bg-purple-400/15 text-purple-300" : "bg-purple-100 text-purple-700")
                                : (dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/10 text-[#006874]")
                          }`}
                        >
                          mcp:{k.tier}
                        </span>
                      )}
                      {k.tier && k.tier !== "admin" && k.email && (() => {
                        const a = webAlign(k);
                        return (
                          <span
                            data-testid={`key-web-align-badge-${k.id}`}
                            title={a.state === "none"
                              ? "No TourismIntel account linked to this email"
                              : a.state === "ok"
                                ? "Aligned with the web plan"
                                : "MISMATCH — key tier differs from the web plan (auto-reconciled at boot / every 6h)"}
                            className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${
                              a.state === "ok"
                                ? (dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                                : a.state === "mismatch"
                                  ? (dm ? "bg-rose-400/15 text-rose-300" : "bg-rose-100 text-rose-700")
                                  : (dm ? "bg-slate-700 text-slate-400" : "bg-slate-200 text-slate-500")
                            }`}
                          >
                            {a.label}
                          </span>
                        );
                      })()}
                      {(k.scopes || []).includes("autopilot:write") && (
                        <span
                          data-testid={`key-write-scope-${k.id}`}
                          title="This key can call /api/external/autopilot/approve and /reject"
                          className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-100 text-amber-700"}`}
                        >
                          autopilot:write
                        </span>
                      )}
                      {(k.scopes || []).includes("documents:write") && (
                        <span
                          data-testid={`key-docs-scope-${k.id}`}
                          title="This key can push documents to /api/external/pulse/documents (Pulse Doc Catalog)"
                          className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-semibold ${dm ? "bg-sky-400/15 text-sky-300" : "bg-sky-100 text-sky-700"}`}
                        >
                          documents:write
                        </span>
                      )}
                    </div>
                    <div className={`flex items-center gap-3 mt-1 text-[10px] font-mono ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                      <span>{k.key_prefix}…</span>
                      {k.last_used_at ? (
                        <span className="flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          Last used {new Date(k.last_used_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span>Never used</span>
                      )}
                      <span>Created {new Date(k.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {k.is_active && k.tier && (
                    <select
                      value={k.tier}
                      onChange={e => changeTier(k.id, e.target.value)}
                      disabled={changingTier === k.id}
                      data-testid={`key-tier-select-${k.id}`}
                      title="Change MCP tier (manual upgrade/downgrade)"
                      className={`flex-shrink-0 px-2 py-1.5 rounded-lg text-[11px] border-0 outline-none cursor-pointer ${dm ? "bg-[#252e2f] text-slate-300" : "bg-white text-[#3b494c]"}`}
                    >
                      <option value="open">open</option>
                      <option value="pro">pro</option>
                      <option value="admin">admin</option>
                    </select>
                  )}
                  {k.is_active && (
                    <button
                      onClick={() => revokeKey(k.id, k.name)}
                      disabled={revoking === k.id}
                      data-testid={`revoke-key-btn-${k.id}`}
                      title="Revoke key"
                      className={`flex-shrink-0 p-2 rounded-lg transition-colors ${dm ? "text-slate-600 hover:bg-red-400/10 hover:text-red-400" : "text-[#e7e8e9] hover:bg-red-50 hover:text-red-500"}`}
                    >
                      {revoking === k.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Beta PRO access requests */}
        <McpProRequestsCard dm={dm} />

        {/* MCP usage charts */}
        <McpUsageCard dm={dm} />

        {/* MCP audit trail */}
        <McpAuditCard dm={dm} />

        {/* Security note */}
        <div className={`rounded-2xl p-4 ${dm ? "bg-[#1a2122]/50 border border-white/5" : "bg-[#f3f4f5]/50 border border-black/5"}`}>
          <p className={`text-xs ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
            Keys are stored as <strong>SHA-256 hashes</strong> — the raw key is never saved in the database. Revoked keys stop working immediately.
          </p>
        </div>

      </div>
    </TabsContent>
  );
}
